import { Amazon } from "./Amazon";
import { Database } from "./Database";
import { Network } from "./Network";
import { Twitch } from "./Twitch";
import { PlayFab, InventoryResponse } from './PlayFab';
import { Google } from './Google';

export class Account {
  protected static userId: string = '';
  protected static userName: string = '';
  protected static displayName: string = '';
  protected static userPortrait: string = '';
  protected static loggedIn: boolean = false;
  protected static inventory: any[] = [];
  protected static inventoryLoaded: boolean = false;
  protected static callbacks: any = {};
  protected static platformUserId: string = '';
  protected static masterConnection: string = '';
  protected static linkedProviders: Record<string, boolean> = {};
  protected static season: any = null;
  protected static seasonTime: number = 0;
  private static driftData: any = null;

  public static async loginWithCustomId(id: string, skipFinishLogin: boolean = false) {
    const result = await PlayFab.loginWithCustomId(id);
    if (result.success) {
      Account.userId = PlayFab.getPlayerId();
      // Use the provided id as a display name seed (strip any prefix like 'guest:')
      const name = id.includes(':') ? id.split(':', 2)[1] : id;
      Account.userName = name;
      Account.displayName = name;
      Account.loggedIn = true;
      // no platform id for custom id
      localStorage.setItem('AutoLogin', JSON.stringify({ platform: 'custom', user: id, token: '' }));
      // Try to set Title DisplayName to the chosen name so profile and header reflect it
      try {
        await PlayFab.post('Client/UpdateUserTitleDisplayName', { DisplayName: name });
      } catch {}
      
      if (!skipFinishLogin) {
        await Account.finishLogin();
      }
    }
    return { success: result.success };
  }

  public static async loginWithEmailAddress(email: string, password: string) {
    let response = await PlayFab.post('LoginWithEmailAddress', {
      TitleId: 'D93AF',
      Email: email,
      Password: password,
      RequireBothUsernameAndEmail: false
    });

    return { success: (response.status == 200), body: response.body };
  }

  public static async loginWithTwitch() {
    Twitch.login();
  }

  public static async continueLoginWithTwitch(accessToken: string) {
    let response = await PlayFab.loginWithTwitch(accessToken);

    if (response.success) {
      Twitch.setAccessToken(accessToken);
      let userResponse = await Twitch.getUser();
      if (userResponse.success)
        await Account.finishLoginWithTwitch(accessToken, userResponse);
    }

    return { success: response.success };
  }

  public static async finishLoginWithTwitch(accessToken: string, userResponse: any) {
    Account.userId = PlayFab.getPlayerId();
    Account.userName = userResponse.body.data[0].login;
    Account.displayName = await Account.getOrCreateUsername(userResponse.body.data[0].id, userResponse.body.data[0].display_name);
    Account.userPortrait = userResponse.body.data[0].profile_image_url;
    Account.platformUserId = userResponse.body.data[0].id;
    Account.loggedIn = true;

    let cache = {
      platform: 'twitch',
      user: userResponse.body.data[0].id,
      token: accessToken
    };

    localStorage.setItem('AutoLogin', JSON.stringify(cache));

    await Account.finishLogin();
  }

  public static async loginWithGoogle() {
    Google.login();
  }

  public static async continueLoginWithGoogle(idToken: string) {
    let response = await PlayFab.loginWithOpenIdConnect(idToken, 'Google');

    if (response.success) {
      Google.setIdToken(idToken);
      let userResponse = await Google.getUser();
      if (userResponse.success)
        await Account.finishLoginWithGoogle(idToken, userResponse);
    }

    return { success: response.success };
  }

  public static async finishLoginWithGoogle(idToken: string, userResponse: any) {
    Account.userId = PlayFab.getPlayerId();
    const generatedUsername = await Account.getOrCreateUsername(userResponse.body.sub, userResponse.body.name);
    Account.userName = generatedUsername;
    Account.displayName = generatedUsername;
    Account.userPortrait = userResponse.body.picture;
    Account.platformUserId = userResponse.body.sub;
    Account.loggedIn = true;

    let cache = {
      platform: 'google',
      user: userResponse.body.sub,
      token: idToken
    };

    localStorage.setItem('AutoLogin', JSON.stringify(cache));

    await Account.finishLogin();
  }

  public static async continueLoginWithAuth0(idToken: string, userInfo: any) {
    console.log("Auth0 userInfo:", userInfo);
    try {
      const platform = Account.getPrimaryConnection(userInfo) || 'auth0';
      Account.masterConnection = platform;
      Account.linkedProviders = {};
      if (platform) {
        Account.linkedProviders[platform.toLowerCase()] = true;
      }

      const preferredLogin = Account.getAuth0DisplayName(userInfo);
      const preferredDisplay = preferredLogin;

      const response = await PlayFab.loginWithOpenIdConnect(idToken, 'Auth0');
      console.log("PlayFab loginWithOpenIdConnect response:", response);

      if (!response.success) {
        console.error('Failed to login with PlayFab:', response);
        return { success: false, error: 'Failed to login with PlayFab' };
      }

      const playFabId = PlayFab.getPlayerId();

      Account.userId = playFabId;
      const platformKey = userInfo.sub;
      const avatarUrl = userInfo.picture;
      
      // Run username creation and avatar update in parallel
      const [generatedUsername] = await Promise.all([
        Account.getOrCreateUsername(platformKey, preferredDisplay || preferredLogin),
        avatarUrl 
          ? PlayFab.updateAvatarUrl(avatarUrl).then(success => {
              if (success) {
                console.log("Avatar URL updated successfully in PlayFab");
              } else {
                console.error("Failed to update avatar URL in PlayFab");
              }
              return success;
            }).catch(err => {
              console.error("Failed to update avatar URL in PlayFab", err);
              return false;
            })
          : Promise.resolve(false)
      ]);
      
      Account.userName = generatedUsername;
      Account.displayName = generatedUsername;
      Account.userPortrait = avatarUrl;
      Account.platformUserId = platformKey;
      Account.loggedIn = true;

      let cache = {
        platform: 'auth0',
        user: platformKey,
        token: idToken
      };

      localStorage.setItem('AutoLogin', JSON.stringify(cache));

      await Account.finishLogin();
      return { success: true };
    } catch (error: unknown) {
      console.error('Error during Auth0 login:', error);
      if (error instanceof Error) {
        return { success: false, error: error.message };
      } else {
        return { success: false, error: 'An unknown error occurred' };
      }
    }
  }

  public static async linkProviderWithAuth0(userInfo: any): Promise<{ success: boolean; provider?: string; error?: string; renamedLegacy?: boolean }> {
    if (!PlayFab.hasValidSession()) {
      return { success: false, error: 'No active PlayFab session' };
    }

    const platform = Account.getPrimaryConnection(userInfo);
    if (!platform) {
      return { success: false, error: 'Unable to determine provider' };
    }

    const sessionTicket = PlayFab.getSessionTicket();
    if (!sessionTicket) {
      return { success: false, error: 'Session ticket unavailable' };
    }

    const preferredLogin = Account.getAuth0DisplayName(userInfo);
    const payload = {
      subject: userInfo.sub,
      sessionTicket,
      login: preferredLogin,
      displayName: preferredLogin,
      platform
    };

    try {
      const response = await Amazon.post('auth', 'link-provider-account', payload);
      if (response.status !== 200 || !response.body?.success) {
        console.warn('Provider linking failed', response);
        return { success: false, error: 'Linking failed', provider: platform };
      }

      Account.linkedProviders[platform.toLowerCase()] = true;

      try {
        await Account.finishLogin();
        await Account.refreshLinkedAccountsFromServer();
      } catch (err) {
        console.warn('Failed to refresh account after provider linking', err);
      }

      return { success: true, provider: platform, renamedLegacy: !!response.body?.legacyDisplayRenamed };
    } catch (error) {
      console.error('Error linking provider account:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Linking error', provider: platform };
    }
  }

  public static async getInventory(force: boolean = false) {
    if (!force && Account.inventoryLoaded)
      return Account.inventory;

    Account.inventory = [];
    Account.inventoryLoaded = true;

    let allItems: any[] = [];
    let continuationToken: string | null = null;

    do {
      const response: InventoryResponse = await PlayFab.getInventory(continuationToken);

      if (response.success && response.items) {
        allItems = allItems.concat(response.items);
        continuationToken = response.continuationToken || null;
      } else {
        console.error("Failed to fetch inventory");
        return Account.inventory;
      }
    } while (continuationToken);

    for (let i = 0; i < allItems.length; i++) {
      let serverId = allItems[i].Id;
      let amount = allItems[i].Amount;
      let asset = Database.getAssetByServerId(serverId);
      if (asset)
        Account.inventory.push({ id: asset.id, amount })
    }

    return Account.inventory;
  }

  public static addAssetToInventoryCache(id: string, amount: number) {
    if (!Account.inventoryLoaded)
      return;

    let existing = Account.inventory.find((a: any) => a.id == id);
    if (existing)
      existing.amount += amount;
    else
      Account.inventory.push({ id, amount });
  }

  // Garage configuration storage key
  private static GARAGE_CONFIG_KEY = 'GarageConfig';

  // Per-car cosmetics loadout type
  public static readonly CosmeticsLoadout = {
    paint: null as string | null,
    hoodDecal: null as string | null,
    sideDecal: null as string | null,
    underglow: null as string | null,
    trail: null as string | null,
    booster: null as string | null,
    ram: null as string | null,
    hat: null as string | null,
    horn: null as string | null,
  };

  // Save garage configuration to PlayFab via CloudScript with server-side validation
  // The server validates that all cosmetics are owned by the player before saving
  // Supports per-car loadouts and trophy layout
  public static async saveGarageConfig(config: {
    version: number;
    currentCar: string;
    carLoadouts: Record<string, {
      paint: string | null;
      hoodDecal: string | null;
      sideDecal: string | null;
      underglow: string | null;
      trail: string | null;
      booster: string | null;
      ram: string | null;
      hat: string | null;
      horn: string | null;
    }>;
    trophyLayout?: (string | null)[]; // Array of trophy asset IDs for slots 0-4
  }): Promise<boolean> {
    if (!Account.isLoggedIn()) {
      console.warn('Cannot save garage config: not logged in');
      return false;
    }

    try {
      // Use CloudScript for server-side validation of cosmetics ownership
      const response = await PlayFab.executeCloudScript('saveGarageConfig', { config });
      
      if (response.success && response.data) {
        const result = response.data;
        if (result.strippedCount > 0) {
          console.warn(`Garage config saved: ${result.strippedCount} unowned cosmetics were removed`);
        }
        return result.success;
      }
      
      // Fallback to direct save if CloudScript fails (backwards compatibility)
      console.warn('CloudScript validation failed, falling back to direct save');
      const fallbackResponse = await PlayFab.updatePlayerTitleData({
        Data: {
          [Account.GARAGE_CONFIG_KEY]: JSON.stringify(config)
        }
      });
      return fallbackResponse.success;
    } catch (error) {
      console.error('Error saving garage config:', error);
      return false;
    }
  }

  // Load garage configuration from PlayFab user data
  // Returns config that may be old format (single cosmetics) or new format (per-car loadouts)
  public static async getGarageConfig(): Promise<any | null> {
    if (!Account.isLoggedIn()) {
      console.warn('Cannot load garage config: not logged in');
      return null;
    }

    try {
      const response = await PlayFab.getUserTitleData([Account.GARAGE_CONFIG_KEY]);
      if (response.success && response.data && response.data[Account.GARAGE_CONFIG_KEY]) {
        const configData = response.data[Account.GARAGE_CONFIG_KEY] as any;
        // PlayFab returns { Value: "..." } for each key
        const configString = typeof configData === 'object' && configData.Value 
          ? configData.Value 
          : configData;
        return JSON.parse(configString);
      }
      return null;
    } catch (error) {
      console.error('Error loading garage config:', error);
      return null;
    }
  }

  public static hasAutoLogin() {
    let item = localStorage.getItem('AutoLogin');
    return item != null;
  }

  public static isCustomIdLogin(): boolean {
    try {
      const item = localStorage.getItem('AutoLogin');
      if (!item) return false;
      const cache = JSON.parse(item);
      return cache.platform === 'custom';
    } catch {
      return false;
    }
  }

  // public static async autoLogin() {
  //   let item = localStorage.getItem('AutoLogin');
  //   if (!item)
  //     return { success: false };

  //   let cache = JSON.parse(item);
  //   let token = cache.token;

  //   if (cache.platform == 'twitch') {
  //     Twitch.setAccessToken(token);
  //     let userResponse = await Twitch.getUser();
  //     if (userResponse.success) {
  //       let response = await PlayFab.loginWithTwitch(token);
  //       if (response.success) {
  //         await Account.finishLoginWithTwitch(token, userResponse);
  //         return response;
  //       }
  //     }
  //   } else if (cache.platform == 'google') {
  //     Google.setIdToken(token);
  //     let userResponse = await Google.getUser();
  //     if (userResponse.success) {
  //       let response = await PlayFab.loginWithOpenIdConnect(token, 'GoogleOpenId');
  //       if (response.success) {
  //         await Account.finishLoginWithGoogle(token, userResponse);
  //         return response;
  //       }
  //     }
  //   } else if (cache.platform == 'auth0') {
  //     // try {
  //     //   console.log("Attempting Auth0 auto-login...");
  //     //   let response = await PlayFab.loginWithOpenIdConnect(token, 'Auth0');
  //     //   console.log("Auth0 auto-login PlayFab response:", response);
  //     //   if (response.success) {
  //     //     await Account.continueLoginWithAuth0(token, { sub: cache.user });
  //     //     return response;
  //     //   }
  //     // } catch (error) {
  //     //   console.error('Error during Auth0 auto-login:', error);
  //     // }
  //   }

  //   localStorage.removeItem('AutoLogin');
  //   return { success: false };
  // }

  public static logout() {
    // Clear account state
    Account.userId = '';
    Account.userName = '';
    Account.displayName = '';
    Account.userPortrait = '';
    Account.platformUserId = '';
    Account.loggedIn = false;
    Account.inventory = [];
    Account.inventoryLoaded = false;
    Account.season = null;
    Account.seasonTime = 0;
    Account.masterConnection = '';
    Account.linkedProviders = {};

    // Clear local storage
    localStorage.removeItem('AutoLogin');
    localStorage.removeItem(Account.LINKED_PROVIDERS_CACHE_KEY);

    // Make callbacks to notify components of the logout
    Account.makeCallbacks();

    // Clear PlayFab session
    PlayFab.clearSession();
  }

  public static async registerUser(email: string, password: string) {
    let response = await PlayFab.post('RegisterPlayFabUser', {
      TitleId: 'D93AF',
      Email: email,
      Password: password,
      RequireBothUsernameAndEmail: false
    });

    return { success: (response.status == 200), body: response.body };
  }

  public static isLoggedIn(): boolean {
    return Account.loggedIn || PlayFab.hasValidSession();
  }

  public static getUserId(): string {
    return Account.userId || PlayFab.getPlayerId();
  }

  public static getPlatformUserId(): string {
    return Account.platformUserId;
  }

  public static getMasterConnection(): string {
    if (!Account.masterConnection) {
      Account.syncMasterConnectionFromAutoLogin();
    }
    return Account.masterConnection;
  }

  public static isProviderLinked(provider: string): boolean {
    if (!Account.linkedProviders || Object.keys(Account.linkedProviders).length === 0) {
      Account.syncMasterConnectionFromAutoLogin();
    }
    return !!Account.linkedProviders[provider.toLowerCase()];
  }

  public static getUserName(): string {
    const name = Account.displayName || Account.userName;
    if (name) return name;
    try {
      const item = localStorage.getItem('AutoLogin');
      if (item) {
        const cache = JSON.parse(item);
        if (cache.platform === 'custom' && typeof cache.user === 'string') {
          const parts = cache.user.split(':');
          if (parts.length === 2 && parts[1]) return parts[1];
        }
      }
    } catch {}
    return '';
  }

  public static getUserPortrait(): string {
    return Account.userPortrait;
  }

  public static addCallback(id: string, callback: Function) {
    Account.callbacks[id] = callback;
  }

  public static removeCallback(id: string) {
    delete Account.callbacks[id];
  }

  protected static makeCallbacks() {
    let ids = Object.keys(Account.callbacks);
    for (let i = 0; i < ids.length; i++) {
      let callback = Account.callbacks[ids[i]];
      if (callback)
        callback();
    }
  }

  public static async loadSeason() {
    // Only skip if we have cached data AND it's fresh (less than 5 minutes old)
    let elapsed = performance.now() - Account.seasonTime;
    if (Account.season && elapsed < (5 * 60 * 1000))
      return;

    let response = await PlayFab.executeCloudScript('getSeason');

    if (response.success) {
      Account.season = response.data.season;
      Account.seasonTime = performance.now();
    }
  }

  public static getSeason() {
    return Account.season;
  }

  public static getSeasonPlayerData() {
    if (!Account.season) return { level: 0, current: 0, max: 0 };
    let xpPerLevel = Account.season.levels[0];
    let level = Math.floor(Account.season.xp / xpPerLevel);
    let current = Account.season.xp - (level * xpPerLevel);
    let max = xpPerLevel;
    return { level, current, max }
  }

  public static getSeasonQuests() {
    return Account.season ? Account.season.quests : [];
  }

  public static getSeasonMilestones() {
    return Account.season ? Account.season.milestones : [];
  }

  public static getSeasonRewards() {
    return Account.season ? Account.season.rewards : [];
  }

  public static async claimQuestReward(qi: number) {
    let response = await PlayFab.executeCloudScript('claimQuestReward', { quest: qi });
    if (response.success) {
      Account.season.xp = response.data.xp;
      Account.season.quests[qi].claimed = true;
      if (qi != 0)
        Account.season.quests[0].progress++;
    }
    return { success: response.success };
  }

  public static async claimMilestoneReward(mi: number) {
    let response = await PlayFab.executeCloudScript('claimMilestoneReward', { milestone: mi });
    if (response.success) {
      Account.season.xp = response.data.xp;
      Account.season.milestones[mi].level++;
      if (response.data.goal)
        Account.season.milestones[mi].goal = response.data.goal;
    }
    return { success: response.success };
  }

  public static async claimSeasonReward(level: number) {
    let response = await PlayFab.executeCloudScript('claimSeasonReward', { level });
    let success = response.success && response.data && response.data.success;
    if (success)
      Account.season.rewards[level].claimed = true;
    return { success };
  }

  public static isDeveloperLoggedIn(): boolean {
    let developers = [
      'cryptojack21',
      'sammysnake7',
      'sixbitglitch',
      'turisstation',
      'superdopetv',
      'reverendtate',
      'rob_boffin',
      'beanpole1904',
      'gizmogorilla',
      'sammysnake',
      // 'raks_wax',
      // 'dobermann_twitch'
    ];

    return Account.isLoggedIn() && developers.indexOf(Account.getUserName().toLowerCase()) !== -1;
  }

  private static async getOrCreateUsername(platformId: string, platformName?: string): Promise<string> {
    try {
      console.log(`Starting getOrCreateUsername for platformId: ${platformId}, platformName: ${platformName}`);

      const accountInfoResponse = await PlayFab.getAccountInfo();
      const targetName = Account.sanitizeDisplayNameCandidate(platformName);

      if (accountInfoResponse.status === 200 &&
        accountInfoResponse.body?.data?.AccountInfo?.TitleInfo?.DisplayName) {
        const existingDisplayName = accountInfoResponse.body.data.AccountInfo.TitleInfo.DisplayName;
        const sanitizedExisting = Account.sanitizeDisplayNameCandidate(existingDisplayName);

        if (!targetName || sanitizedExisting === targetName) {
          console.log("Existing TitleDisplayName found:", existingDisplayName);
          return existingDisplayName;
        }

        console.log("Existing TitleDisplayName differs from desired name; will update", {
          existingDisplayName,
          targetName
        });
      }

      console.log("No existing TitleDisplayName found. Using platform name or generating new username...");
      
      // Extract connection from platformId (userInfo.sub)
      const parts = platformId.split('|');
      const connection = parts[0] === 'oauth2' ? parts[1] : parts[0];
      console.log("Extracted connection:", connection);
      
      const sanitizedPlatformName = targetName;
      let newUsername = '';
      if (sanitizedPlatformName) {
        newUsername = sanitizedPlatformName;
      }

      switch (connection) {
        case 'google-oauth2':
        case 'twitch':
        case 'discord':
          if (!newUsername) {
            console.log("Platform name unavailable, generating random username for:", connection);
            newUsername = Account.generateRandomUsername();
          }
          break;
        default:
          console.log("Using default case with platformName:", platformName);
          newUsername = newUsername || sanitizedPlatformName || Account.generateRandomUsername();
      }
      
      console.log("Initial new username:", newUsername);

      console.log("Updating TitleDisplayName in PlayFab...");
      const updateResponse = await PlayFab.post('Client/UpdateUserTitleDisplayName', { DisplayName: newUsername });
      console.log("Update TitleDisplayName response:", updateResponse);

      if (updateResponse.status === 200) {
        const updatedDisplayName = updateResponse.body.data.DisplayName;
        console.log("TitleDisplayName successfully updated to:", updatedDisplayName);
        return updatedDisplayName;
      } else {
        console.error("Failed to update TitleDisplayName:", updateResponse);
        throw new Error("Failed to update TitleDisplayName");
      }
    } catch (error) {
      console.error("Error in getOrCreateUsername:", error);
      if (platformName) {
        console.log("Falling back to original platform name:", platformName);
        return platformName;
      }
      throw error;
    }
  }

  private static generateRandomUsername(): string {
    const adjectives = ['Happy', 'Lucky', 'Sunny', 'Clever', 'Swift', 'Brave', 'Bright'];
    const nouns = ['Panda', 'Tiger', 'Eagle', 'Dolphin', 'Fox', 'Wolf', 'Bear'];
    const randomNumber = Math.floor(Math.random() * 1000);

    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];

    return `${randomAdjective}${randomNoun}${randomNumber}`;
  }

  private static sanitizeDisplayNameCandidate(name?: string): string {
    if (!name) return '';
    let candidate = String(name).trim();
    if (!candidate) return '';

    const atIndex = candidate.indexOf('@');
    if (atIndex > 0) {
      candidate = candidate.substring(0, atIndex);
    }

    candidate = candidate.replace(/\s+/g, '');
    candidate = candidate.replace(/[^A-Za-z0-9_\-]/g, '');

    if (candidate.length > 25) {
      candidate = candidate.substring(0, 25);
    }

    if (candidate.length < 3) {
      return '';
    }

    return candidate;
  }

  private static getAuth0DisplayName(userInfo: any): string {
    if (!userInfo) return '';

    const sanitize = Account.sanitizeDisplayNameCandidate;

    const connection = Account.getPrimaryConnection(userInfo);
    const lower = connection.toLowerCase();

    switch (lower) {
      case 'twitch':
      case 'discord':
        return sanitize(userInfo.auth0_username);
      // Add new providers here as needed, e.g. case 'google': return sanitize(userInfo.name);
      default:
        return sanitize(userInfo.nickname);
    }
  }

  private static getPrimaryConnection(userInfo: any): string {
    if (!userInfo) return '';

    if (Array.isArray(userInfo.identities) && userInfo.identities.length > 0) {
      const identity = userInfo.identities[0];
      if (identity && typeof identity.connection === 'string') {
        return identity.connection;
      }
      if (identity && typeof identity.provider === 'string') {
        return identity.provider;
      }
    }

    if (typeof userInfo.sub === 'string') {
      const parts = userInfo.sub.split('|');
      if (parts.length >= 2) {
        return parts[1];
      }
    }

    return '';
  }

  private static deriveConnectionFromPlatformKey(platformKey: string): string {
    if (!platformKey) return '';
    const parts = platformKey.split('|');
    if (parts.length === 0) return '';
    if (parts[0] === 'oauth2' && parts.length >= 3) {
      return parts[1];
    }
    return parts[0];
  }

  private static extractOpenIdProvider(linkedAccount: any): string {
    if (!linkedAccount) return '';

    if (typeof linkedAccount.Platform === 'string' && linkedAccount.Platform.toLowerCase() === 'openidconnect') {
      if (typeof linkedAccount.PlatformUserId === 'string') {
        const parts = linkedAccount.PlatformUserId.split('|');
        if (parts.length >= 3 && parts[0] === 'oauth2') {
          return parts[1];
        }
      }
    }

    if (typeof linkedAccount.PlatformUserId === 'string') {
      const parts = linkedAccount.PlatformUserId.split('|');
      if (parts.length >= 3 && parts[0] === 'oauth2') {
        return parts[1];
      }
    }

    return '';
  }

  private static deriveConnectionFromLinkedAccount(linkedAccount: any): string {
    if (!linkedAccount) return '';

    const platformRaw = linkedAccount.Platform || linkedAccount.platform;
    if (typeof platformRaw === 'string') {
      const platform = platformRaw.toLowerCase();

      if ((platform === 'custom' || platform.indexOf('custom') !== -1) && typeof linkedAccount.PlatformUserId === 'string') {
        return Account.deriveConnectionFromPlatformKey(linkedAccount.PlatformUserId);
      }

      if (platform === 'servercustomid' && typeof linkedAccount.PlatformUserId === 'string') {
        return Account.deriveConnectionFromPlatformKey(linkedAccount.PlatformUserId);
      }

      return platform;
    }

    if (typeof linkedAccount.PlatformUserId === 'string') {
      return Account.deriveConnectionFromPlatformKey(linkedAccount.PlatformUserId);
    }

    return '';
  }

  private static syncMasterConnectionFromAutoLogin() {
    try {
      const raw = localStorage.getItem('AutoLogin');
      if (!raw) return;

      const cache = JSON.parse(raw);
      if (!cache) return;

      if (!Account.masterConnection && cache.platform === 'auth0' && typeof cache.user === 'string' && cache.user) {
        const connection = Account.deriveConnectionFromPlatformKey(cache.user);
        if (connection) {
          Account.masterConnection = connection;
          Account.linkedProviders[connection.toLowerCase()] = true;
        }
      } else if (!Account.masterConnection && typeof cache.platform === 'string' && cache.platform) {
        Account.masterConnection = cache.platform;
        Account.linkedProviders[cache.platform.toLowerCase()] = true;
      } else if (Account.masterConnection) {
        Account.linkedProviders[Account.masterConnection.toLowerCase()] = true;
      }
    } catch (err) {
      console.warn('Failed to sync master connection from AutoLogin', err);
    }
  }

  public static async updateDisplayName(newDisplayName: string): Promise<boolean> {
    try {
      console.log("Updating display name to:", newDisplayName);
      const updateResponse = await PlayFab.post('Client/UpdateUserTitleDisplayName', { DisplayName: newDisplayName });
      console.log("Update display name response:", updateResponse);
  
      if (updateResponse.status === 200) {
        Account.displayName = newDisplayName;
        Account.makeCallbacks();
        return true;
      } else {
        console.error("Failed to update display name:", updateResponse);
        return false;
      }
    } catch (error) {
      console.error("Error updating display name:", error);
      return false;
    }
  }
  
  public static async finishLogin() {
    // Display name is already set by getOrCreateUsername or login flow
    // Only fetch account info if we don't have a display name yet
    if (!Account.displayName || !Account.userName) {
      const accountInfoResponse = await PlayFab.getAccountInfo();
      if (accountInfoResponse.status === 200 && 
          accountInfoResponse.body?.data?.AccountInfo?.TitleInfo?.DisplayName) {
        const displayName = accountInfoResponse.body.data.AccountInfo.TitleInfo.DisplayName;
        Account.displayName = displayName;
        Account.userName = displayName;
      }
    }

    // Set master connection from local cache (fast, no network)
    Account.syncMasterConnectionFromAutoLogin();
    
    // Linked accounts and season data load on-demand when needed
    Account.makeCallbacks();
  }

  private static LINKED_PROVIDERS_CACHE_KEY = 'LinkedProvidersCache';

  public static async refreshLinkedAccountsFromServer() {
    const sessionTicket = PlayFab.getSessionTicket();
    if (!sessionTicket) {
      console.warn('Cannot refresh linked accounts without session ticket');
      Account.loadLinkedProvidersFromCache();
      return;
    }

    const response = await Amazon.post('auth', 'get-linked-accounts', { sessionTicket });
    if (response.status !== 200 || !response.body?.success) {
      console.warn('Auth service get-linked-accounts failed', response);
      Account.loadLinkedProvidersFromCache();
      return;
    }

    const linkedAccounts = Array.isArray(response.body.linkedAccounts) ? response.body.linkedAccounts : [];
    Account.linkedProviders = {};
    for (const account of linkedAccounts) {
      if (account && account.platform) {
        Account.linkedProviders[String(account.platform).toLowerCase()] = true;
      }
    }

    const master = response.body.masterConnection;
    if (master) {
      Account.masterConnection = String(master);
      Account.linkedProviders[String(master).toLowerCase()] = true;
    } else {
      Account.syncMasterConnectionFromAutoLogin();
    }

    // Cache the linked providers
    Account.saveLinkedProvidersToCache();
  }

  private static saveLinkedProvidersToCache() {
    try {
      const cache = {
        providers: Account.linkedProviders,
        master: Account.masterConnection,
        timestamp: Date.now()
      };
      localStorage.setItem(Account.LINKED_PROVIDERS_CACHE_KEY, JSON.stringify(cache));
    } catch (err) {
      console.warn('Failed to cache linked providers', err);
    }
  }

  private static loadLinkedProvidersFromCache(): boolean {
    try {
      const cached = localStorage.getItem(Account.LINKED_PROVIDERS_CACHE_KEY);
      if (!cached) {
        Account.syncMasterConnectionFromAutoLogin();
        return false;
      }
      
      const cache = JSON.parse(cached);
      // Cache valid for 24 hours
      if (cache.timestamp && Date.now() - cache.timestamp < 24 * 60 * 60 * 1000) {
        if (cache.providers) {
          Account.linkedProviders = cache.providers;
        }
        if (cache.master) {
          Account.masterConnection = cache.master;
        }
        return true;
      }
      
      Account.syncMasterConnectionFromAutoLogin();
      return false;
    } catch (err) {
      console.warn('Failed to load linked providers cache', err);
      Account.syncMasterConnectionFromAutoLogin();
      return false;
    }
  }

  public static getLinkedProvidersFromCache(): Record<string, boolean> {
    if (Object.keys(Account.linkedProviders).length === 0) {
      Account.loadLinkedProvidersFromCache();
    }
    return Account.linkedProviders;
  }

  public static async loadDrift() {
    try {
      const result = await PlayFab.executeCloudScript('getDriftData');
      if (result.success) {
        this.driftData = result.data?.data;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading drift data:', error);
      return false;
    }
  }

  public static getDriftData() {
    return this.driftData;
  }

  public static async saveDriftData(data: any) {
    try {
      const result = await PlayFab.executeCloudScript('saveDriftData', { data });
      if (result.success) {
        this.driftData = data;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving drift data:', error);
      return false;
    }
  }

  static async getCurrencies(): Promise<{ [key: string]: number }> {
    const response = await PlayFab.getCurrencyItems();
    if (response.success && response.items) {
      return response.items.reduce((acc: { [key: string]: number }, item: any) => {
        acc[item.Id] = item.Amount;
        return acc;
      }, {});
    }
    return {};
  }
}